function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'react-collapse';
import { ChevronUpIcon, ChevronDownIcon } from '@domain-group/fe-co-icon';
import { useSendEvent } from '@domain-group/fe-co-allhomes-analytics';
import { Button } from '@domain-group/fe-co-allhomes-design-system';
import ListingFeatures from './features';
import * as styles from './listing-description.style';
import { jsx as ___EmotionJSX } from "@emotion/core";
var TIMEOUT = 50;
var SIZE_OFFSET = 38; // combination of button height and margin
var SERVER_RENDERED_DESCRIPTION_LENGTH = 410;
var ListingDescription = props => {
  var _props$description;
  var _useSendEvent = useSendEvent(),
    sendEvent = _useSendEvent.sendEvent;
  var serverRenderedDescription = (_props$description = props.description) === null || _props$description === void 0 ? void 0 : _props$description.substring(0, SERVER_RENDERED_DESCRIPTION_LENGTH);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isOpened = _useState2[0],
    setIsOpened = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLongDescription = _useState4[0],
    setLongDescription = _useState4[1];
  var _useState5 = useState(props.clientRendered ? serverRenderedDescription : props.description),
    _useState6 = _slicedToArray(_useState5, 2),
    description = _useState6[0],
    setDescription = _useState6[1];
  var descriptionRef = useRef(); // Used to determine the length of the description
  var contentBlock = useRef(); // Used to measure the total size of the content, inluding features
  var container = useRef(); // Used to measure the visible size of the component
  var timer = useRef(); // Used to persist a timeout handler between renders
  var isDescriptionTextLong = props.description.length > SERVER_RENDERED_DESCRIPTION_LENGTH;
  var updateLongDescription = useCallback(() => {
    var _descriptionRef$curre;
    setLongDescription(descriptionRef.current && (((_descriptionRef$curre = descriptionRef.current) === null || _descriptionRef$curre === void 0 ? void 0 : _descriptionRef$curre.clientHeight) || 0) > props.previewHeight);
  }, [descriptionRef, props.previewHeight]);
  useEffect(() => {
    updateLongDescription();
  }, [updateLongDescription]);
  useEffect(() => () => global.clearTimeout(timer.current), []);
  useEffect(() => {
    window.addEventListener('resize', updateLongDescription);
    return () => window.removeEventListener('resize', updateLongDescription);
  });

  // Work around the fact that react-collapse doesn't fire onRest unless it's collapsed to size 0
  // by basically reimplementing onRest behaviour in the summary component itself
  var handleResizeProgress = () => {
    var _container$current, _contentBlock$current;
    var containerHeight = (_container$current = container.current) === null || _container$current === void 0 ? void 0 : _container$current.clientHeight;
    var contentHeight = (_contentBlock$current = contentBlock.current) === null || _contentBlock$current === void 0 ? void 0 : _contentBlock$current.clientHeight;
    if (isOpened ? containerHeight >= contentHeight : containerHeight <= props.previewHeight + SIZE_OFFSET) {
      props.onResizeFinished();
    } else {
      timer.current = global.setTimeout(() => handleResizeProgress(), TIMEOUT);
    }
  };
  var toggleOpenDescription = () => {
    if (props.clientRendered) {
      setDescription(isOpened ? serverRenderedDescription : props.description);
    }
    setIsOpened(!isOpened);
  };
  var handleViewClick = () => {
    if (!isOpened) {
      sendEvent('show-more');
    }
    toggleOpenDescription();
  };
  var isCollapsible = props.clientRendered && isDescriptionTextLong || !props.clientRendered && isLongDescription;
  return ___EmotionJSX("div", {
    css: styles.description,
    "data-testid": "description"
  }, isCollapsible ? ___EmotionJSX("div", {
    ref: container,
    css: styles.collapse(props.previewHeight)
  }, ___EmotionJSX(Collapse, {
    isOpened: isOpened,
    onWork: handleResizeProgress
  }, ___EmotionJSX("section", {
    ref: contentBlock
  }, ___EmotionJSX("div", {
    ref: descriptionRef,
    css: styles.content,
    dangerouslySetInnerHTML: {
      __html: description
    } // eslint-disable-line react/no-danger
  }), ___EmotionJSX(ListingFeatures, {
    features: props.featureList
  })), !isOpened && ___EmotionJSX("div", {
    css: styles.fade
  })), ___EmotionJSX(Button, {
    css: styles.descriptionButton,
    appearance: "secondaryNeutral",
    size: "small",
    icon: isOpened ? ___EmotionJSX(ChevronUpIcon, {
      size: "small"
    }) : ___EmotionJSX(ChevronDownIcon, {
      size: "small"
    }),
    iconPosition: "right",
    onClick: handleViewClick
  }, isOpened ? props.viewLessLabel : props.viewMoreLabel)) : ___EmotionJSX("section", null, ___EmotionJSX("div", {
    ref: descriptionRef,
    css: styles.content,
    dangerouslySetInnerHTML: {
      __html: description
    } // eslint-disable-line react/no-danger
  }), ___EmotionJSX(ListingFeatures, {
    features: props.featureList
  })));
};
ListingDescription.propTypes = {
  description: PropTypes.string.isRequired,
  previewHeight: PropTypes.number,
  viewMoreLabel: PropTypes.string,
  viewLessLabel: PropTypes.string,
  onResizeFinished: PropTypes.func,
  featureList: PropTypes.arrayOf(PropTypes.string),
  clientRendered: PropTypes.bool.isRequired
};
ListingDescription.defaultProps = {
  previewHeight: 150,
  viewMoreLabel: 'View more',
  viewLessLabel: 'View less',
  onResizeFinished: () => {},
  featureList: []
};
export default ListingDescription;